'use client';

import React from 'react';
import { MODE } from '@/utils/classroomUtils';

export type CareerFilterKey =
  | 'todos'
  | 'online'
  | 'presencial-ba'
  | 'presencial-tucuman';

type CareerFilterOption = {
  key: CareerFilterKey;
  label: string;
};

export const CAREER_FILTERS: Record<CareerFilterKey, CareerFilterOption> = {
  todos: { key: 'todos', label: 'Todos' },
  online: { key: 'online', label: 'Online' },
  'presencial-ba': {
    key: 'presencial-ba',
    label: 'Presencial Buenos Aires',
  },
  'presencial-tucuman': {
    key: 'presencial-tucuman',
    label: 'Presencial Tucuman',
  },
};

const BUENOS_AIRES_LOCATION_ALIASES = [
  'buenos aires',
  'caba',
  'capital federal',
  'bs as',
  'baires',
];

const TUCUMAN_LOCATION_ALIAS = 'tucuman';

const normalizeText = (value?: string) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const isBuenosAiresLocation = (location?: string) => {
  const normalizedLocation = normalizeText(location);
  return BUENOS_AIRES_LOCATION_ALIASES.some((alias) =>
    normalizedLocation.includes(alias)
  );
};

const isTucumanLocation = (location?: string) =>
  normalizeText(location).includes(TUCUMAN_LOCATION_ALIAS);

export type FilterableCareer = {
  mode?: string;
  officeLocation?: string;
};

export const filterCareersByKey = <T extends FilterableCareer>(
  careers: T[],
  filterKey: CareerFilterKey | ''
) => {
  if (!filterKey) {
    return careers;
  }

  return careers.filter((career) => {
    const normalizedMode = normalizeText(career.mode);
    const isPresencial = normalizedMode === normalizeText(MODE.PRESENCIAL);

    if (filterKey === CAREER_FILTERS.online.key) {
      return !isPresencial;
    }

    if (filterKey === CAREER_FILTERS['presencial-ba'].key) {
      return isPresencial && isBuenosAiresLocation(career.officeLocation);
    }

    if (filterKey === CAREER_FILTERS['presencial-tucuman'].key) {
      return isPresencial && isTucumanLocation(career.officeLocation);
    }

    return true;
  });
};

interface CareerFiltersProps {
  activeFilter: CareerFilterKey | '';
  onFilterChange: (filterKey: CareerFilterKey) => void;
  className?: string;
}

const CareerFilters: React.FC<CareerFiltersProps> = ({
  activeFilter,
  onFilterChange,
  className = '',
}) => {
  return (
    <div id="select-available-classrooms" className={`scroll-mt-24 flex flex-wrap justify-center gap-2 ${className}`.trim()}>
      {Object.values(CAREER_FILTERS).map((filter) => {
        const isActive =
          filter.key === CAREER_FILTERS.todos.key
            ? !activeFilter
            : activeFilter === filter.key;

        return (
          <button
            key={filter.key}
            type="button"
            onClick={() => onFilterChange(filter.key)}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
              isActive
                ? 'border-red-600 bg-red-600 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:border-red-400 hover:text-red-600'
            }`}
            aria-pressed={isActive}
          >
            {filter.label}
          </button>
        );
      })}
    </div>
  );
};

export default CareerFilters;